import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import styles from './password-generator.component.module.scss';
import { genStyles } from '../converter-layout/generator-layout';

interface PasswordState {
    length: number;
    uppercase: boolean;
    lowercase: boolean;
    digits: boolean;
    symbols: boolean;
    password: string;
    copied: boolean;
    error: string;
}

const CHARSETS = {
    uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    lowercase: 'abcdefghijklmnopqrstuvwxyz',
    digits: '0123456789',
    symbols: '!@#$%^&*()_+-=[]{}|;:,.<>?',
};

function generatePassword(length: number, opts: Pick<PasswordState, 'uppercase' | 'lowercase' | 'digits' | 'symbols'>): string {
    let pool = '';
    if (opts.uppercase) pool += CHARSETS.uppercase;
    if (opts.lowercase) pool += CHARSETS.lowercase;
    if (opts.digits) pool += CHARSETS.digits;
    if (opts.symbols) pool += CHARSETS.symbols;
    if (!pool) return '';
    const arr = new Uint32Array(length);
    crypto.getRandomValues(arr);
    return Array.from(arr, v => pool[v % pool.length]).join('');
}

function getStrength(length: number, charsetCount: number): { label: string; cls: string } {
    const bits = length * Math.log2(charsetCount || 1);
    if (bits < 36) return { label: 'Weak', cls: styles.weak };
    if (bits < 60) return { label: 'Medium', cls: styles.medium };
    if (bits < 100) return { label: 'Strong', cls: styles.strong };
    return { label: 'Very Strong', cls: styles.veryStrong };
}

export default class PasswordGeneratorComponent extends Component<{ feature?: string }, PasswordState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;

    state: PasswordState = {
        length: 16, uppercase: true, lowercase: true, digits: true, symbols: true,
        password: '', copied: false, error: '',
    };

    componentDidMount() { this.generate(); }

    componentDidUpdate(_: unknown, prev: PasswordState) {
        if (prev.length !== this.state.length || prev.uppercase !== this.state.uppercase ||
            prev.lowercase !== this.state.lowercase || prev.digits !== this.state.digits ||
            prev.symbols !== this.state.symbols) {
            this.generate();
        }
    }

    generate = () => {
        const { length, uppercase, lowercase, digits, symbols } = this.state;
        if (!uppercase && !lowercase && !digits && !symbols) {
            this.setState({ password: '', error: 'Select at least one character set' });
            return;
        }
        this.setState({ password: generatePassword(length, { uppercase, lowercase, digits, symbols }), error: '' });
    };

    copy = () => {
        navigator.clipboard.writeText(this.state.password).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    toggle = (key: 'uppercase' | 'lowercase' | 'digits' | 'symbols') => {
        this.setState(s => ({ ...s, [key]: !s[key] } as PasswordState));
    };

    render() {
        const { length, uppercase, lowercase, digits, symbols, password, copied, error } = this.state;
        let poolSize = 0;
        if (uppercase) poolSize += 26;
        if (lowercase) poolSize += 26;
        if (digits) poolSize += 10;
        if (symbols) poolSize += CHARSETS.symbols.length;
        const strength = getStrength(length, poolSize);

        return (
            <div className="row">
                <div className="col-12">
                    <div className={genStyles.section}>
                        <label className={sharedStyles.label}>Length: {length}</label>
                        <div className={styles.sliderRow}>
                            <span>8</span>
                            <input type="range" className={styles.slider} min={8} max={128} value={length}
                                onChange={e => this.setState({ length: +e.target.value })} aria-label="Password length" />
                            <span>128</span>
                            <span className={styles.lengthValue}>{length}</span>
                        </div>

                        <div className={styles.checkboxes}>
                            {(['uppercase', 'lowercase', 'digits', 'symbols'] as const).map(k => (
                                <label key={k} className={styles.checkLabel}>
                                    <input type="checkbox" checked={this.state[k]} onChange={() => this.toggle(k)} />
                                    {k.charAt(0).toUpperCase() + k.slice(1)}
                                </label>
                            ))}
                        </div>

                        {error && <div className={sharedStyles.errorMessage} aria-live="polite">⚠ {error}</div>}
                    </div>

                    <div className={genStyles.section}>
                        <div className={styles.output}>{password}</div>

                        <div className={styles.actions}>
                            <button className={sharedStyles.buttonPrimary} onClick={this.generate}>↻ Regenerate</button>
                            <button className={`${sharedStyles.copyButton} ${copied ? sharedStyles.copyButtonSuccess : ''}`}
                                onClick={this.copy} disabled={!password}>
                                {copied ? '✓ Copied' : 'Copy'}
                            </button>
                        </div>

                        {password && <span className={`${styles.strength} ${strength.cls}`}>{strength.label}</span>}
                    </div>
                </div>
            </div>
        );
    }
}
